/**
 * CareAndGoalsCard — Participant Chart integration card.
 *
 * Phase 1.4 of Care Plan & Goals v2. Consumes the `care_and_goals` block
 * appended to `/api/participants/{id}/overview-summary` when the
 * `care_plan_goals_v2_enabled` flag is ON. When the block is absent the
 * component renders nothing (no duplicate participant chart, no duplicate
 * data).
 */
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ClipboardCheck, Target, FileText, ShieldAlert, AlertOctagon, Pill,
  CalendarClock, Users, ArrowRight,
} from 'lucide-react';

function MetricChip({ icon: Icon, label, value, tone = 'default', testid, onClick }) {
  const toneClass = {
    default: 'text-foreground',
    warn: 'text-amber-600',
    danger: 'text-red-600',
    success: 'text-emerald-600',
    muted: 'text-muted-foreground',
  }[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-left hover:bg-accent hover:border-primary/40 transition-colors"
    >
      <Icon className={`h-3.5 w-3.5 ${toneClass}`} />
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className={`text-sm font-semibold ${toneClass}`}>{value}</span>
      </div>
    </button>
  );
}

export function CareAndGoalsCard({ careAndGoals, onNavigate }) {
  if (!careAndGoals) return null;

  const cp = careAndGoals.active_care_plan;
  const goalsBy = careAndGoals.goals_by_status || {};
  const ack = careAndGoals.staff_acknowledgement || { ack_required_count: 0, ack_received_count: 0 };
  const ackPct = ack.ack_required_count > 0
    ? Math.round((ack.ack_received_count / ack.ack_required_count) * 100)
    : 0;

  const reviewTone = !cp
    ? 'muted'
    : cp.review_overdue
      ? 'danger'
      : (cp.days_until_review != null && cp.days_until_review <= 30 ? 'warn' : 'success');

  return (
    <Card data-testid="care-and-goals-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Care & Goals</h3>
          </div>
          <Button
            size="sm"
            variant="ghost"
            data-testid="care-and-goals-open-tab"
            onClick={() => onNavigate?.('goals')}
            className="h-7 text-xs"
          >
            Open Goals <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </div>

        {/* Active care plan row */}
        <div className="rounded-md border bg-muted/30 px-3 py-2" data-testid="care-and-goals-active-plan">
          {cp ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="outline" className="font-mono text-[10px]">{cp.plan_no}</Badge>
              <span className="font-medium truncate max-w-[260px]">{cp.title}</span>
              <Badge data-testid="care-plan-status-badge">{cp.status}</Badge>
              <span className="text-xs text-muted-foreground">v{cp.version}</span>
              {cp.review_due_date && (
                <span className={`text-xs flex items-center gap-1 ${reviewTone === 'danger' ? 'text-red-600' : reviewTone === 'warn' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                  <CalendarClock className="h-3 w-3" />
                  Review {cp.review_due_date}
                  {cp.review_overdue && ' · overdue'}
                  {!cp.review_overdue && cp.days_until_review != null && ` · ${cp.days_until_review}d`}
                </span>
              )}
              {cp.last_acknowledged_at && (
                <span className="text-[10px] text-muted-foreground">
                  Last ack {String(cp.last_acknowledged_at).slice(0, 10)}
                </span>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No active care plan yet.</p>
          )}
        </div>

        {/* Goals progress bar */}
        <div data-testid="care-and-goals-progress-row">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">
              {careAndGoals.active_goals_count} active goal{careAndGoals.active_goals_count === 1 ? '' : 's'}
              {' · '}
              {Object.entries(goalsBy).map(([k, v]) => `${k.toLowerCase()}: ${v}`).join(' · ') || 'no entries'}
            </span>
            <span className="font-medium" data-testid="care-and-goals-avg-progress">
              {careAndGoals.goal_progress_avg_pct}% avg
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${careAndGoals.goal_progress_avg_pct}%` }}
            />
          </div>
        </div>

        {/* Metric chip grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <MetricChip
            icon={Target}
            label="Goal evidence"
            value={careAndGoals.goal_evidence_count}
            testid="metric-goal-evidence"
            onClick={() => onNavigate?.('goals')}
          />
          <MetricChip
            icon={FileText}
            label="Linked notes"
            value={careAndGoals.linked_progress_notes_count}
            testid="metric-linked-notes"
            onClick={() => onNavigate?.('notes')}
          />
          <MetricChip
            icon={ShieldAlert}
            label="Linked risks"
            value={careAndGoals.linked_risks_count}
            tone={careAndGoals.linked_risks_count > 0 ? 'warn' : 'muted'}
            testid="metric-linked-risks"
            onClick={() => onNavigate?.('risks')}
          />
          <MetricChip
            icon={AlertOctagon}
            label="Open incidents"
            value={careAndGoals.linked_incidents_count}
            tone={careAndGoals.linked_incidents_count > 0 ? 'danger' : 'muted'}
            testid="metric-linked-incidents"
            onClick={() => onNavigate?.('incidents')}
          />
          <MetricChip
            icon={Pill}
            label="Medication alerts"
            value={careAndGoals.medication_alerts_count}
            tone={careAndGoals.medication_alerts_count > 0 ? 'warn' : 'muted'}
            testid="metric-medication-alerts"
            onClick={() => onNavigate?.('preferences')}
          />
          <MetricChip
            icon={CalendarClock}
            label="Review due"
            value={cp?.days_until_review != null ? `${cp.days_until_review}d` : '—'}
            tone={reviewTone}
            testid="metric-review-due"
            onClick={() => onNavigate?.('careplans')}
          />
          <MetricChip
            icon={Users}
            label="Staff ack."
            value={`${ack.ack_received_count}/${ack.ack_required_count} (${ackPct}%)`}
            tone={ackPct === 100 ? 'success' : (ack.ack_required_count > 0 ? 'warn' : 'muted')}
            testid="metric-staff-ack"
            onClick={() => onNavigate?.('careplans')}
          />
          {(careAndGoals.funding_category_links || []).length > 0 && (
            <MetricChip
              icon={ClipboardCheck}
              label="Funding links"
              value={careAndGoals.funding_category_links.length}
              testid="metric-funding-links"
              onClick={() => onNavigate?.('funding')}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
